import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useTeam } from "@/lib/team";
import heroImg from "@/assets/equipo-hero.jpg";
import { ArrowRight, Check } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import teamJesus from "@/assets/team-jesus.jpg";
import teamLucia from "@/assets/team-lucia.jpg";
import teamAlvaro from "@/assets/team-alvaro.jpg";
import teamMarta from "@/assets/team-marta.jpg";
import teamSergio from "@/assets/team-sergio.jpg";
import teamCarmen from "@/assets/team-carmen.jpg";

export const Route = createFileRoute("/about/equipo")({
  component: TeamPage,
  head: () => ({
    meta: [
      { title: "Nuestro equipo — Bosque Suspendido" },
      { name: "description", content: "Conoce al equipo de Bosque Suspendido: monitores, técnicos de seguridad y coordinadores que hacen posible la aventura entre los árboles en Sevilla." },
      { property: "og:title", content: "El equipo de Bosque Suspendido" },
      { property: "og:description", content: "Monitores, seguridad, eventos y mantenimiento: las personas que cuidan de tu aventura en La Juliana." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/about/equipo" },
      { property: "og:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/about/equipo" }],
  }),
});

function TeamPage() {
  const { tk } = useT();
  const hero = useMedia("about.hero", heroImg, "El equipo de Bosque Suspendido en el bosque");
  const team = useTeam();

  const p1 = useMedia("team.1.portrait", teamJesus, team[0]?.portrait.alt ?? "");
  const p2 = useMedia("team.2.portrait", teamLucia, team[1]?.portrait.alt ?? "");
  const p3 = useMedia("team.3.portrait", teamAlvaro, team[2]?.portrait.alt ?? "");
  const p4 = useMedia("team.4.portrait", teamMarta, team[3]?.portrait.alt ?? "");
  const p5 = useMedia("team.5.portrait", teamSergio, team[4]?.portrait.alt ?? "");
  const p6 = useMedia("team.6.portrait", teamCarmen, team[5]?.portrait.alt ?? "");
  const portraits = [p1, p2, p3, p4, p5, p6];

  return (
    <Layout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-primary">
        <img
          src={hero.url}
          alt=""
          aria-hidden
          width={1920}
          height={1080}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-primary/10" aria-hidden />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 w-full text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold-soft font-bold">
            {tk("equipo.kicker", "Nuestro equipo")}
          </span>
          <h1 className="font-serif font-extrabold text-5xl md:text-7xl mt-5 max-w-4xl mx-auto text-primary-foreground leading-tight drop-shadow-md">
            {tk("equipo.title.a", "Las personas detrás de ")}
            <span className="text-gold-soft">Bosque Suspendido</span>
          </h1>
          <p className="mt-6 text-primary-foreground/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            {tk(
              "equipo.intro",
              "Monitores, técnicos de seguridad, coordinadores de eventos y cuidadores del bosque. Cada arnés que te pones y cada tirolina que cruzas pasa antes por sus manos.",
            )}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 space-y-24">
        {team.map((m, idx) => (
          <article
            key={m.slug}
            id={m.slug}
            className="scroll-mt-32 grid md:grid-cols-5 gap-12 items-center"
          >
            <div className={`md:col-span-2 relative ${idx % 2 === 1 ? "md:order-2" : ""}`}>
              <div className="absolute -inset-3 rounded-2xl border border-gold/25" />
              <div className="relative rounded-xl overflow-hidden bg-card border border-gold/20 aspect-[3/4]">
                <img
                  src={(portraits[idx] ?? m.portrait).url}
                  alt={portraits[idx]?.alt || m.portrait.alt || m.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  width={768}
                  height={1024}
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <span className="text-xs tracking-[0.4em] uppercase text-gold">
                {m.featured ? tk("equipo.lead", "Fundador") : tk("equipo.member", "Equipo")}
              </span>
              <h2 className="font-serif text-3xl md:text-5xl mt-3">{m.name}</h2>
              <div className="text-sm text-foreground/70 mt-3">{m.role}</div>
              <div className="gold-divider my-6 max-w-[100px]" />
              <p className="text-muted-foreground leading-relaxed">{m.bio}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {m.specialties.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-foreground/80">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </section>

      <section className="relative border-y border-gold/20 bg-cover bg-center" style={{ backgroundImage: `url(${hero.url})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/85 to-primary/70" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center text-primary-foreground">
          <span className="text-xs tracking-[0.4em] uppercase text-gold-soft font-bold">
            {tk("equipo.cta.kicker", "Te esperamos en el bosque")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">
            {tk("equipo.cta.title", "Ven a conocernos en persona")}
          </h2>
          <p className="mt-6 text-primary-foreground/90 leading-relaxed">
            {tk(
              "equipo.cta.text",
              "Reserva tu aventura o escríbenos si tienes dudas: el equipo te ayudará a elegir el circuito perfecto para tu grupo, tu cole o tu empresa.",
            )}
          </p>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-3 wood-btn px-7 py-3.5 text-xs tracking-[0.3em] uppercase"
          >
            {tk("equipo.cta.button", "Reserva tu aventura")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
